/**
 * Groups service — MOCK (CT-5563 shared data layer).
 *
 * Groups (Engineering, AI Ops …) bundle members; membership lives on the
 * members so it stays consistent with `membersService`. Contract TBD
 * (ARP/backend); platform envelope, snake_case bodies.
 */
import {
  envelope,
  errorEnvelope,
  matchesSearch,
  notFoundEnvelope,
} from '../shared/utils/mockServiceHelpers.js';
import { isNameTaken, memberRefError, membershipApi, withMembers } from './collectionHelpers.js';
import { dropFromAllMembers, setMembership } from './membershipHelpers.js';
import { nextId, resetStore, store } from './seeds/store.js';

const KEY = 'group_ids';

/** Test-only: reset the shared mock store (members, roles, groups, grants). */
export const __resetMockGroupsForTests = () => resetStore();

const view = (group) => withMembers(KEY, group);

const findGroup = (id) => store.groups.find((g) => g.id === id);
const nameTaken = (name, exceptId) => isNameTaken(store.groups, name, exceptId);

const validate = (payload, exceptId) => {
  if ('name' in payload) {
    if (!String(payload.name ?? '').trim())
      return errorEnvelope(422, 'NAME_REQUIRED', 'Group name is required.');
    if (nameTaken(payload.name, exceptId))
      return errorEnvelope(409, 'NAME_TAKEN', 'A group with this name already exists.');
  }
  return memberRefError(payload.member_ids);
};

/**
 * Groups for the Groups page; `search.query` matches name + description.
 * TODO(CT-5563 / backend): `GET /people-access/groups` — contract TBD (ARP/backend).
 */
export const listGroups = (params = {}) => {
  const { search = {} } = params;
  const items = store.groups
    .filter((g) => matchesSearch(g, search.query, ['name', 'description']))
    .map(view);
  return Promise.resolve(envelope({ items, total: items.length }, { total: items.length }));
};

/**
 * One group (header + member count).
 * TODO(CT-5563 / backend): `GET /people-access/groups/:id` — contract TBD (ARP/backend).
 */
export const getGroup = (id) => {
  const group = findGroup(id);
  return Promise.resolve(group ? envelope(view(group)) : notFoundEnvelope('Group not found'));
};

/**
 * Create a group (New group modal), optionally with initial `member_ids`.
 * TODO(CT-5563 / backend): `POST /people-access/groups` — contract TBD (ARP/backend).
 */
export const createGroup = (payload = {}) => {
  const error = validate({ name: '', ...payload });
  if (error) return Promise.resolve(error);
  const group = {
    id: nextId('grp'),
    name: payload.name.trim(),
    description: payload.description ?? '',
    created_at: new Date().toISOString(),
  };
  store.groups = [...store.groups, group];
  setMembership(KEY, group.id, payload.member_ids ?? []);
  return Promise.resolve(envelope(view(group)));
};

/**
 * Rename / re-describe a group.
 * TODO(CT-5563 / backend): `PATCH /people-access/groups/:id` — contract TBD (ARP/backend).
 */
export const updateGroup = (id, patch = {}) => {
  const group = findGroup(id);
  if (!group) return Promise.resolve(notFoundEnvelope('Group not found'));
  const error = validate({ ...('name' in patch ? { name: patch.name } : {}) }, id);
  if (error) return Promise.resolve(error);
  const next = { ...group };
  ['name', 'description'].forEach((k) => {
    if (patch[k] !== undefined) next[k] = k === 'name' ? patch[k].trim() : patch[k];
  });
  store.groups = store.groups.map((g) => (g.id === id ? next : g));
  return Promise.resolve(envelope(view(next)));
};

/**
 * Delete a group; it is removed from every member.
 * TODO(CT-5563 / backend): `DELETE /people-access/groups/:id` — contract TBD (ARP/backend).
 */
export const deleteGroup = (id) => {
  if (!findGroup(id)) return Promise.resolve(notFoundEnvelope('Group not found'));
  dropFromAllMembers(KEY, id);
  store.groups = store.groups.filter((g) => g.id !== id);
  return Promise.resolve(envelope({ id, deleted: true }));
};

const groupMembers = membershipApi(KEY, findGroup, 'Group not found');

/**
 * Members of a group (paginated).
 * TODO(CT-5563 / backend): `GET /people-access/groups/:id/members` — contract TBD (ARP/backend).
 */
export const listGroupMembers = groupMembers.list;

/**
 * Add members to a group ("Add members").
 * TODO(CT-5563 / backend): `POST /people-access/groups/:id/members` — contract TBD (ARP/backend).
 */
export const addGroupMembers = groupMembers.add;

/**
 * Remove one member from a group (row trash icon).
 * TODO(CT-5563 / backend): `DELETE /people-access/groups/:id/members/:member_id` — contract TBD (ARP/backend).
 */
export const removeGroupMember = groupMembers.remove;
